import type { BuildContext, Fragment } from "../types.js";
import { dimensionsFor, getSafeZone } from "../format.js";
import { setColorTheme, defaultColorTheme } from "../../brand/theme.js";
import { buildCompositionHtml } from "../composition.js";
import { mergeFragments } from "../types.js";
import { brandBackdrop } from "../primitives/brandBackdrop.js";
import { watermark, type WatermarkPosition } from "../primitives/watermark.js";
import { safeZoneOverlay } from "../primitives/safeZoneOverlay.js";
import { getSceneBuilder, isScenePorted } from "./scenes.js";
import { buildOverlayHtml } from "./overlays.js";
import type { QueueItem } from "../../lib/queue.js";
import { FPS } from "../types.js";
import { tl } from "../timeline.js";
import { clipTransitionEnvelope } from "./transitions.js";

type ResolvedClip = {
  scene: NonNullable<QueueItem["scenes"]>[number];
  fromSec: number;
  durSec: number;
  track: number;
};

/**
 * Mirrors `resolveClipLayout()` in `src/compositions/Reel.tsx` but returns
 * seconds (instead of frames) since Hyperframes' clip attributes are
 * seconds-based.
 */
function resolveLayout(scenes: NonNullable<QueueItem["scenes"]>): ResolvedClip[] {
  const anyExplicit = scenes.some((s) => typeof s.fromSec === "number");
  if (anyExplicit) {
    // Multi-track layout — author chose track indices. Detect intra-track
    // overlaps and bump conflicting clips onto fresh tracks so the
    // Hyperframes lint passes. We never go above track index 7 (overlay
    // tracks are reserved >= 10).
    const out: ResolvedClip[] = scenes.map((s) => ({
      scene: s,
      fromSec: Math.max(0, s.fromSec ?? 0),
      durSec: Math.max(8 / FPS, s.durationSec),
      track: Math.max(0, Math.round(s.track ?? 0)),
    }));
    return spreadTracks(out, 0, 7);
  }
  // Sequential layout — each scene gets its own track so transition fades can
  // freely overlap without triggering Hyperframes' "overlapping_clips_same_track"
  // lint error. The visual stacking still works because Hyperframes paints
  // higher track indices on top.
  let acc = 0;
  const out: ResolvedClip[] = [];
  for (let i = 0; i < scenes.length; i += 1) {
    const s = scenes[i]!;
    const dur = Math.max(8 / FPS, s.durationSec);
    out.push({ scene: s, fromSec: acc, durSec: dur, track: i % 8 });
    acc += dur;
    if (i < scenes.length - 1 && s.transition && s.transition.kind !== "none") {
      const overlap = Math.max(0, Math.min(45, s.transition.durationFrames ?? 8)) / FPS;
      acc -= overlap;
    }
  }
  return out;
}

/**
 * If two clips on the same track overlap (after a small epsilon), bump the
 * later one onto a fresh track. Used to keep multi-track layouts lint-clean
 * even when the author / scene composer didn't separate tracks manually.
 */
function spreadTracks(clips: ResolvedClip[], minTrack: number, maxTrack: number): ResolvedClip[] {
  const sorted = clips
    .map((c, i) => ({ c, i }))
    .sort((a, b) => a.c.fromSec - b.c.fromSec);
  const trackUsage = new Map<number, number>();
  for (const entry of sorted) {
    const { c } = entry;
    let track = Math.max(minTrack, c.track);
    const eps = 1e-3;
    while (track <= maxTrack) {
      const last = trackUsage.get(track) ?? -Infinity;
      if (c.fromSec >= last - eps) {
        c.track = track;
        trackUsage.set(track, c.fromSec + c.durSec);
        break;
      }
      track += 1;
    }
  }
  return clips;
}

function calcReelDurationSec(item: QueueItem): number {
  const scenes = item.scenes ?? [];
  const layout = resolveLayout(scenes);
  let max = 0;
  for (const c of layout) max = Math.max(max, c.fromSec + c.durSec);
  for (const ov of item.overlays ?? []) max = Math.max(max, ov.fromSec + ov.durSec);
  for (const sfx of item.sfxEvents ?? []) max = Math.max(max, sfx.fromSec + sfx.durSec);
  if (item.music?.enabled) {
    const start = item.music.fromSec ?? 0;
    if (typeof item.music.durSec === "number") max = Math.max(max, start + item.music.durSec);
  }
  if (item.voiceover?.enabled) {
    const segs = item.voiceover.segments;
    if (Array.isArray(segs) && segs.length > 0) {
      for (const seg of segs) max = Math.max(max, seg.fromSec + seg.durSec);
    } else {
      const start = item.voiceover.fromSec ?? 0;
      if (typeof item.voiceover.durSec === "number") {
        max = Math.max(max, start + item.voiceover.durSec);
      }
    }
  }
  return Math.max(2, max);
}

type AudioSrc = {
  src: string;
  fromSec: number;
  durSec: number;
  volume: number;
  rate?: number;
  trimStartSec?: number;
  fadeInSec?: number;
  fadeOutSec?: number;
  loop?: boolean;
  label: string;
  /**
   * Welche Sound-Klasse? Wird vom Player verwendet, um selektives Muten zu
   * ermöglichen (Stumm-Schalter für SFX darf Musik nicht stoppen, und
   * umgekehrt). Wenn nicht gesetzt → "sfx" (sicheres Default).
   */
  kind?: "sfx" | "music" | "voiceover";
};

/**
 * Hyperframes treats `<audio class="clip">` elements as timed media. The
 * runtime drives currentTime via the captured timeline, applying `data-volume`
 * and respecting `data-playback-start`. Fade-ins/outs need a GSAP volume
 * ramp because there's no `data-fade-in` attribute (see comments below).
 */
function buildAudioHtml(audios: AudioSrc[]): { html: string; tweens: string[] } {
  const tweens: string[] = [];
  // Each audio clip needs a unique track index to avoid the
  // duplicate_audio_track lint warning. We start at track 20 (above the
  // overlay range) and increment per clip.
  const lines = audios.map((a, i) => {
    const id = `aud-${i}`;
    const audioTrack = 20 + i;
    const kind = a.kind ?? "sfx";
    const attrs: string[] = [
      `id="${id}"`,
      `class="clip"`,
      `src="${a.src}"`,
      `data-start="${a.fromSec.toFixed(4)}"`,
      `data-duration="${a.durSec.toFixed(4)}"`,
      `data-volume="${a.volume.toFixed(3)}"`,
      `data-audio-kind="${kind}"`,
      `data-track-index="${audioTrack}"`,
      `preload="auto"`,
      `playsinline`,
    ];
    if (a.trimStartSec && a.trimStartSec > 0) {
      attrs.push(`data-media-start="${a.trimStartSec.toFixed(4)}"`);
    }
    if (a.rate && a.rate !== 1) {
      attrs.push(`data-playback-rate="${a.rate.toFixed(3)}"`);
    }
    if (a.loop) attrs.push("loop");
    // Fade-Attribute zusätzlich zu den GSAP-Tweens emittieren — der Player
    // berechnet Fades zur Laufzeit selbst aus diesen Werten und vermeidet
    // damit den Konflikt zwischen GSAP-Volume-Tween und der per-Frame-
    // baseVolume-Reassertion in tick(). Der Producer (Render-Pfad) liest
    // weiterhin die Tweens; beide Pfade liefern das gleiche Resultat.
    if (a.fadeInSec && a.fadeInSec > 0) {
      attrs.push(`data-fade-in="${a.fadeInSec.toFixed(4)}"`);
    }
    if (a.fadeOutSec && a.fadeOutSec > 0) {
      attrs.push(`data-fade-out="${a.fadeOutSec.toFixed(4)}"`);
    }

    // Fade in/out — emit GSAP volume tweens on the element's `.volume` prop.
    if (a.fadeInSec && a.fadeInSec > 0) {
      tweens.push(
        tl.fromTo(
          `#${id}`,
          { volume: 0 },
          { volume: a.volume, duration: a.fadeInSec, ease: "power1.out" },
          a.fromSec,
        ),
      );
    }
    if (a.fadeOutSec && a.fadeOutSec > 0) {
      tweens.push(
        tl.to(
          `#${id}`,
          { volume: 0, duration: a.fadeOutSec, ease: "power1.in" },
          a.fromSec + a.durSec - a.fadeOutSec,
        ),
      );
    }
    return `<audio ${attrs.join(" ")}></audio>`;
  });
  return { html: lines.join("\n"), tweens };
}

/**
 * Map a built-in music bed key to its asset path. Music files live under
 * `public/sounds/music/` and we stage them into `assets/sounds/music/` for
 * the Hyperframes producer.
 */
/**
 * Normalise a runtime media URL to a Hyperframes asset-relative path so the
 * producer can serve it from the staged work directory. Handles the same
 * cases as `pickMediaSrc()` for scene media but covers audio specifically:
 *
 *  - Absolute http(s) / data / blob → kept as-is.
 *  - `/audio-library/<f>` → `audio-library/<f>` (relative).
 *  - `/static/<f>` → `assets/<f>` (the public producer convention).
 *  - `/uploads/<f>` → `assets/uploads/<f>`.
 *  - Anything else starting with `/` has its leading slash stripped.
 */
function normaliseAudioUrl(url: string): string {
  if (/^https?:\/\//i.test(url) || url.startsWith("data:") || url.startsWith("blob:")) return url;
  const staticMatch = url.match(/^\/static\/(.+)$/);
  if (staticMatch) return `assets/${staticMatch[1]}`;
  const uploadsMatch = url.match(/^\/uploads\/(.+)$/);
  if (uploadsMatch) return `assets/uploads/${uploadsMatch[1]}`;
  if (url.startsWith("/")) return url.slice(1);
  return url;
}

function musicSrc(item: QueueItem): string | null {
  if (!item.music) return null;
  if (item.music.src) return normaliseAudioUrl(item.music.src);
  if (item.music.bed) return `assets/sounds/music/${item.music.bed}.wav`;
  return null;
}

function voiceoverSrc(item: QueueItem): string | null {
  if (!item.voiceover) return null;
  if (item.voiceover.src) return normaliseAudioUrl(item.voiceover.src);
  return null;
}

function sfxSrc(ev: NonNullable<QueueItem["sfxEvents"]>[number]): string {
  if (ev.src) return normaliseAudioUrl(ev.src);
  return `assets/sounds/${ev.sound}.wav`;
}

export function buildReelHtml(item: QueueItem, ctx: Omit<BuildContext, "durationSec">): string {
  // Render-Theme pro Reel anwenden, bevor irgendeine Szene/Backdrop ihre
  // Farben liest. Da der gesamte Build synchron ist, ist das globale Umschalten
  // der geteilten Palette sicher; am Ende auf den CI-Default zurücksetzen.
  const theme = item.theme ?? ctx.theme ?? defaultColorTheme;
  setColorTheme(theme);
  const themedCtx = { ...ctx, theme };
  ctx = themedCtx;

  const scenes = item.scenes ?? [];

  // Empty reel → return a valid placeholder composition so live previews in the
  // Composer don't crash before the user has added any scenes.
  if (scenes.length === 0) {
    const emptyHtml = buildCompositionHtml({
      compositionId: `${item.id}-empty`,
      ctx: { ...ctx, durationSec: 1 },
      fragments: [
        {
          html: `<div class="hf-empty-reel" data-start="0" data-duration="1" data-track-index="0" style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;background:#0a0a0c;color:#666;font-family:Inter,system-ui,sans-serif;font-size:24px;">Keine Szenen</div>`,
        },
      ],
    });
    setColorTheme(defaultColorTheme);
    return emptyHtml;
  }

  const durationSec = calcReelDurationSec(item);
  const fullCtx: BuildContext = { ...ctx, durationSec };
  const { width, height } = dimensionsFor(ctx.format);

  // Reel background — solid dark base (Brand-BG `#06070b`). Scenes have
  // their own opaque backdrops on top, this layer is only visible (a) before
  // the first scene starts, (b) after the last one ends, and (c) for the
  // ~10 frames a scene wrap fades out at the end of its clip. Using a solid
  // dark base means those fade-outs always reveal *black*, never the bright
  // accent gradient — which previously caused a jarring purple/teal flash
  // between adjacent scenes (especially noticeable behind bright-screen
  // scenes like `laptopMockup` cutting into a dark `signOff`).
  const bg = brandBackdrop(fullCtx, "reel-bg", { variant: "solid" });

  // Per-clip scene rendering.
  const layout = resolveLayout(scenes);
  const clipFragments: Fragment[] = [];
  const clipHtmls: string[] = [];
  layout.forEach((clip, i) => {
    // Per-Szene-Theme: `inputs.extras.theme` ("dark"|"light") überschreibt das
    // Reel-Theme NUR für diese eine Szene. Da jede Szene ihren eigenen
    // full-bleed Backdrop zeichnet und ihre Farben (inkl. Per-Clip-Logo)
    // zur Build-Zeit liest, wirkt das Umschalten exakt auf diesen Clip —
    // gemischte Dark/Light-Szenen in einem Reel sind damit möglich.
    const rawClipTheme = clip.scene.inputs?.extras?.theme;
    const clipTheme = rawClipTheme === "light" ? "light" : rawClipTheme === "dark" ? "dark" : theme;
    setColorTheme(clipTheme);

    const builder = getSceneBuilder(clip.scene.sceneId);
    const prefix = `c${i}`;
    const frag = builder({
      ctx: fullCtx,
      clipPrefix: prefix,
      inputs: clip.scene.inputs ?? {},
      clipDurationSec: clip.durSec,
      naturalDurationSec: clip.scene.naturalDurationSec,
      playbackRate: clip.scene.playbackRate ?? 1,
      clipStartSec: clip.fromSec,
    });
    clipFragments.push(frag);

    // Clip visibility envelope. Every scene wrapper is `position:absolute;
    // inset:0` so without an external runtime they would all stack visible
    // simultaneously. We drive opacity from the timeline:
    //   - hidden by default (`opacity:0` inline style)
    //   - flipped on at `clip.fromSec` (or a crossfade ramp)
    //   - flipped off at `clip.fromSec + clip.durSec` (after any outro fade)
    // This way it works identically for:
    //   • the in-browser Composer player (which drives `tl.seek()` only)
    //   • the @hyperframes/producer (Puppeteer + GSAP)
    //   • the standalone hyperframes preview / studio.
    //
    // Performance: zusätzlich zur opacity togglen wir `visibility` —
    // inaktive Szenen werden mit `visibility:hidden` aus der Render-
    // Pipeline genommen. Sonst behalten Szenen mit teuren Effekten
    // (SVG-Strokes, backdrop-filter, große box-shadow-Glows) ihre GPU-
    // Layer auch bei `opacity:0`, was nach mehreren Loops zu sichtbarem
    // Stottern führen kann.
    // Übergangs-Hülle (Intro-Motion + Outro-Fade + visibility). `none` =
    // harter Schnitt; alle anderen Kinds (crossfade, wipe*, slide*, zoom*,
    // blurDissolve, glideUp, flash) bekommen eine echte sichtbare
    // Choreografie. Siehe `clipTransitionEnvelope`.
    const endSec = clip.fromSec + clip.durSec;
    const env = clipTransitionEnvelope({
      wrapSel: `#${prefix}-wrap`,
      kind: clip.scene.transition?.kind ?? "none",
      durFrames: clip.scene.transition?.durationFrames ?? 8,
      fromSec: clip.fromSec,
      endSec,
      clipDurSec: clip.durSec,
    });
    clipFragments.push({ html: "", tweens: env.tweens, init: env.init });

    // Per-Clip Watermark: das Logo wird PRO SZENE gerendert und ist auch
    // PRO SZENE konfigurierbar (Position, Größe, Opazität, Padding, Offset,
    // freie Mittelpunkt-Koordinate). Die globale `item.watermark` dient nur
    // noch als Master-Switch (`enabled`) und als Default für Szenen ohne
    // eigene Override-Werte. Per-Scene-Werte liegen unter
    // `inputs.extras.wm*` (siehe `resolveSceneWatermark()`).
    //
    // Sichtbarkeitslogik:
    //  - `extras.hideLogo === true`  → Watermark in dieser Scene aus.
    //  - `extras.hideLogo === false` → Watermark in dieser Scene an
    //    (überschreibt auch den Default-Off von „Logo-Scenes").
    //  - `extras.hideLogo === undefined` (nicht gesetzt) → Default:
    //    Watermark an, ausser die Scene rendert ihr eigenes Logo
    //    (`SCENES_WITH_OWN_LOGO`) — dann doppeln wir nicht.
    const wmCfg = item.watermark;
    const globalWmOn = !!wmCfg?.enabled;
    const hideLogoFlag = clip.scene.inputs?.extras?.hideLogo;
    const ownLogoScene = SCENES_WITH_OWN_LOGO.has(clip.scene.sceneId);
    const showLogoInClip =
      globalWmOn &&
      hideLogoFlag !== true &&
      (hideLogoFlag === false || !ownLogoScene);
    let clipWatermarkHtml = "";
    if (showLogoInClip && wmCfg) {
      const eff = resolveSceneWatermark(wmCfg, clip.scene.inputs?.extras);
      const clipWm = watermark({
        id: `${prefix}-wm`,
        width,
        height,
        enabled: true,
        position: eff.position,
        opacity: eff.opacity,
        size: eff.size,
        paddingPx: eff.paddingPx,
        offsetX: eff.offsetX,
        offsetY: eff.offsetY,
        xPct: eff.xPct,
        yPct: eff.yPct,
      });
      // data-scene-uid sorgt dafür, dass das PlayerEditOverlay das jeweilige
      // Clip-Watermark eindeutig findet (für Live-DOM-Mutation während Drag).
      clipWatermarkHtml = clipWm.html.replace(
        `id="${prefix}-wm"`,
        `id="${prefix}-wm" data-scene-uid="${clip.scene.uid}" data-clip-wm="1"`,
      );
    }

    clipHtmls.push(
      `<div id="${prefix}-wrap" class="clip" data-start="${clip.fromSec.toFixed(4)}" data-duration="${clip.durSec.toFixed(4)}" data-track-index="${clip.track}" data-scene-uid="${clip.scene.uid}" data-scene-id="${clip.scene.sceneId}" style="position:absolute;inset:0;opacity:0;">${frag.html}${clipWatermarkHtml}</div>`,
    );
  });

  // Overlays (text/shape/icon/badge/image — group is skipped).
  type OverlayLayer = NonNullable<QueueItem["overlays"]>[number];
  const overlayLayers: OverlayLayer[] = item.overlays ?? [];
  const overlayHtmls = overlayLayers.map((ov, i) => buildOverlayHtml(ov, i));

  // Visibility envelope per overlay — mirrors the scene wrapper logic above.
  // Without these tweens the overlay's wrapperStyle (`opacity:<n>`) leaves it
  // visible across the ENTIRE reel duration, regardless of fromSec/durSec.
  // We force opacity:0 in init and flip on/off at the time-window edges.
  const overlayInit: string[] = [];
  const overlayTweens: string[] = [];
  overlayLayers.forEach((ov, i) => {
    if (ov.hidden || ov.kind === "group") return;
    const id = `ov-${i}`;
    const op = typeof ov.opacity === "number" ? ov.opacity : 1;
    const start = Math.max(0, ov.fromSec);
    const end = Math.max(start, ov.fromSec + ov.durSec);
    overlayInit.push(`gsap.set("#${id}", { opacity: 0 });`);
    overlayTweens.push(`tl.set("#${id}", { opacity: ${op} }, ${start.toFixed(4)});`);
    overlayTweens.push(`tl.set("#${id}", { opacity: 0 }, ${end.toFixed(4)});`);
  });

  // SFX & music as <audio class="clip"> elements.
  // Wichtig: `applyMaster` skaliert NUR die Lautstärke. Das `muted`-Flag wird
  // bewusst NICHT mehr beim Build in `data-volume="0"` umgesetzt — sonst
  // löst jeder Mute-Toggle einen Composition-Rebuild + iframe-Reload aus
  // (sichtbares Bildstottern, hörbarer Musik-Aussetzer). Stattdessen taggen
  // wir jedes Audio-Element via `data-audio-kind="sfx" | "music"`, und der
  // Player schaltet SFX-Tags zur Laufzeit selektiv stumm.
  const audios: AudioSrc[] = [];
  for (const ev of item.sfxEvents ?? []) {
    audios.push({
      src: sfxSrc(ev),
      fromSec: ev.fromSec,
      durSec: ev.durSec,
      volume: applyMaster((ev.volume ?? 0.6), item.sfxConfig),
      rate: ev.rate,
      trimStartSec: ev.trimStartSec,
      fadeInSec: ev.fadeInSec,
      fadeOutSec: ev.fadeOutSec,
      label: ev.label ?? `sfx ${ev.sound}`,
      kind: "sfx",
    });
  }
  const music = item.music?.enabled ? musicSrc(item) : null;
  if (music && item.music) {
    audios.push({
      src: music,
      fromSec: item.music.fromSec ?? 0,
      durSec: item.music.durSec ?? durationSec,
      volume: item.music.volume,
      trimStartSec: item.music.trimStartSec,
      fadeInSec: item.music.fadeInSec,
      fadeOutSec: item.music.fadeOutSec,
      loop: item.music.loop,
      label: "music",
      kind: "music",
    });
  }
  if (item.voiceover?.enabled && item.voiceover) {
    // Voiceover-Spur: keine Schleife (Sprache wiederholt sich nicht), eigene
    // Audio-Klasse damit Player und Renderer sie unabhängig von Music + SFX
    // muten / regeln können. Pro Segment kann ein eigener `src` gesetzt
    // sein (mehrere Aufnahmen pro Reel) — fällt zurück auf den Track-Level
    // `voiceover.src`. Wenn `segments` fehlt → Legacy-Single-Clip-Pfad.
    const baseLabel = item.voiceover.label ?? "voiceover";
    const baseVolume = item.voiceover.volume ?? 1;
    const baseSrc = voiceoverSrc(item);
    const segs = item.voiceover.segments;
    if (Array.isArray(segs) && segs.length > 0) {
      // ─── Coalesce contiguous segments of the same source ────────────────
      // Wenn der User die Voiceover-Spur in mehrere Stücke geschnitten hat,
      // aber zwei aufeinanderfolgende Segmente
      //   1) aus der gleichen Quelldatei stammen UND
      //   2) auf der Timeline lückenlos aneinander hängen UND
      //   3) auch IM Quellfile lückenlos sind (trimStart[next] == trimStart[prev] + dur[prev])
      // dann hat der User dort in Wahrheit GAR keinen Schnitt gemacht — das
      // Stück ist eigentlich ein durchgängiges Audio. Im Composer hört man
      // das nicht, weil ein einzelnes <audio>-Element den seek nahtlos
      // weiterspielen kann. Beim Render-Concat erzeugt jeder Segment-Bruch
      // jedoch einen winzigen MP3/AAC-Frame-Re-Init → hörbares Knacken /
      // Stottern an den Schnitten. Wir falten solche Folgen vor dem Build
      // wieder zu einem einzigen Clip zusammen.
      type Seg = {
        src: string;
        fromSec: number;
        durSec: number;
        trimStartSec?: number;
        fadeInSec?: number;
        fadeOutSec?: number;
      };
      const eps = 0.01;
      const expanded: Seg[] = segs
        .map((seg): Seg | null => {
          const segSrc = seg.src ? normaliseAudioUrl(seg.src) : baseSrc;
          if (!segSrc) return null;
          return {
            src: segSrc,
            fromSec: seg.fromSec,
            durSec: seg.durSec,
            trimStartSec: seg.trimStartSec,
            fadeInSec: seg.fadeInSec,
            fadeOutSec: seg.fadeOutSec,
          };
        })
        .filter((s): s is Seg => s !== null);
      // In Reihenfolge (timeline) sortieren — segments[] sollte schon
      // chronologisch sein, aber sicher ist sicher.
      expanded.sort((a, b) => a.fromSec - b.fromSec);
      const merged: Seg[] = [];
      for (const cur of expanded) {
        const prev = merged[merged.length - 1];
        const prevTrim = prev?.trimStartSec ?? 0;
        const curTrim = cur.trimStartSec ?? 0;
        const sameSrc = prev?.src === cur.src;
        const tlContiguous = prev != null && Math.abs((prev.fromSec + prev.durSec) - cur.fromSec) < eps;
        const srcContiguous = prev != null && Math.abs((prevTrim + prev.durSec) - curTrim) < eps;
        // Nur mergen, wenn kein interner Fade-Out auf prev / Fade-In auf
        // cur gesetzt ist — sonst würden wir eine vom User gesetzte
        // Audiokurve verlieren.
        const noInternalFade =
          (!prev?.fadeOutSec || prev.fadeOutSec <= 0) &&
          (!cur.fadeInSec || cur.fadeInSec <= 0);
        if (prev && sameSrc && tlContiguous && srcContiguous && noInternalFade) {
          prev.durSec += cur.durSec;
          // Fade-Out des letzten Segments übernimmt der nun verlängerte
          // Block — wir kopieren ihn vom AKTUELLEN (er gilt für das
          // jetzige Block-Ende).
          prev.fadeOutSec = cur.fadeOutSec;
        } else {
          merged.push({ ...cur });
        }
      }
      merged.forEach((seg, idx) => {
        audios.push({
          src: seg.src,
          fromSec: seg.fromSec,
          durSec: seg.durSec,
          volume: baseVolume,
          trimStartSec: seg.trimStartSec,
          fadeInSec: seg.fadeInSec,
          fadeOutSec: seg.fadeOutSec,
          loop: false,
          label: merged.length > 1 ? `${baseLabel} #${idx + 1}` : baseLabel,
          kind: "voiceover",
        });
      });
    } else if (baseSrc) {
      audios.push({
        src: baseSrc,
        fromSec: item.voiceover.fromSec ?? 0,
        durSec: item.voiceover.durSec ?? durationSec,
        volume: baseVolume,
        trimStartSec: item.voiceover.trimStartSec,
        fadeInSec: item.voiceover.fadeInSec,
        fadeOutSec: item.voiceover.fadeOutSec,
        loop: false,
        label: baseLabel,
        kind: "voiceover",
      });
    }
  }
  const audio = buildAudioHtml(audios);

  // ─── Safe-Zone-Durchsetzung (echt, nicht nur Debug) ──────────────────
  // Wenn `safeZone.enabled`, rücken wir den GESAMTEN Szenen-Stack (Inhalt,
  // Overlays UND die Per-Clip-Watermarks) per Padding in die plattform-
  // sichere Box ein. Da `#reel-stack` `position:absolute;inset:0` ist und
  // jeder Clip-Wrap `position:absolute;inset:0` nutzt, lösen die Clips ihr
  // Containing-Block gegen die PADDING-Box von `#reel-stack` auf — der
  // Inhalt wird also automatisch eingerückt, ohne dass eine einzelne Szene
  // angefasst werden muss. Der Reel-Hintergrund (`bg`, full-bleed Sibling)
  // bleibt randlos und füllt die Safe-Zone-Ränder mit Brand-Dunkel.
  const safeOn = !!item.safeZone?.enabled;
  const szArea = safeOn ? getSafeZone(ctx.format, item.safeZone!.platform ?? "general") : null;
  const stackPad = szArea
    ? `padding:${szArea.top}px ${szArea.right}px ${szArea.bottom}px ${szArea.left}px;box-sizing:border-box;`
    : "";

  // Debug-Overlay (rote Streifen / grüner Rahmen) — bewusst AUSSERHALB des
  // gepaddeten Stacks, als Sibling auf vollem Rahmen, damit die markierten
  // Zonen die echten Frame-Koordinaten zeigen.
  const sz = safeOn
    ? safeZoneOverlay({
        id: "reel-sz",
        enabled: true,
        format: ctx.format,
        platform: item.safeZone!.platform ?? "general",
      })
    : { html: "" };

  // Hinweis: Das Watermark wird NICHT mehr global hier gerendert. Es lebt
  // pro Clip im jeweiligen `c<i>-wrap` (siehe oben), damit Logo-Tab und
  // Scene-Toggle „Logo in dieser Scene" synchron arbeiten und das Logo
  // automatisch mit dem Clip-Fade ein-/ausblendet.
  const sceneStackHtml = `<div id="reel-stack" style="position:absolute;inset:0;${stackPad}">
${clipHtmls.join("\n")}
${overlayHtmls.join("\n")}
${audio.html}
</div>
${sz.html}`;

  const merged = mergeFragments([
    bg,
    ...clipFragments,
    { html: "", init: overlayInit.join("\n"), tweens: overlayTweens },
  ]);
  merged.tweens = [...(merged.tweens ?? []), ...audio.tweens];

  const stageHtml = bg.html + "\n" + sceneStackHtml;

  // Composition-Shell (#stage-Textfarbe, body-bg) folgt dem REEL-Theme, nicht
  // dem zuletzt gebauten Clip-Theme.
  setColorTheme(theme);
  const html = buildCompositionHtml({
    compositionId: `Reel-${ctx.format === "9:16" ? "vertical" : ctx.format === "1:1" ? "square" : "landscape"}-${ctx.locale}`,
    title: `Reel · ${item.id}`,
    ctx: fullCtx,
    fragments: [
      { html: "", css: merged.css, init: merged.init, tweens: merged.tweens },
      { html: stageHtml },
    ],
  });

  // Palette nach dem (synchronen) Build wieder auf den CI-Default zurücksetzen,
  // damit ein nachfolgender Build mit anderem/keinem Theme nicht den Light-Zustand erbt.
  setColorTheme(defaultColorTheme);
  return html;
}

function applyMaster(volume: number, master?: QueueItem["sfxConfig"]): number {
  if (!master) return volume;
  // Hinweis: `master.muted` wird hier BEWUSST ignoriert. Mute ist eine
  // reine Runtime-Schicht (Player wendet es per `data-audio-kind="sfx"`
  // an). So müssen wir den Composition-HTML beim Mute nicht neu bauen.
  return Math.max(0, Math.min(1, volume * master.volume));
}

/** Re-export for tests / external smoke calls. */
export { calcReelDurationSec, resolveLayout };
export { isScenePorted };
export { resolveSceneWatermark };
export { SCENES_WITH_OWN_LOGO };

/**
 * Scenes whose visual design already includes a centred / prominent brand
 * logo. For these the per-clip watermark is OFF by default — otherwise the
 * frame ends up with two logos (e.g. Sign-Off shows the brand wordmark in
 * the centre, our 60 px corner watermark would duplicate that).
 *
 * The user can still force-enable the watermark per scene by flipping the
 * Inspector's „Logo in dieser Scene" toggle to „Sichtbar" — that sets
 * `extras.hideLogo = false` which overrides the default-off.
 */
const SCENES_WITH_OWN_LOGO: Set<string> = new Set([
  "signOff",
  "logoStamp",
  "logoBeamStreak",
  "logoCircleSweep",
]);

/**
 * Effektive Watermark-Settings für eine einzelne Scene berechnen:
 * pro-Scene-Override (in `inputs.extras.wm*`) gewinnt über die globalen
 * Defaults aus `item.watermark`. Die Felder sind absichtlich flach in
 * `extras` abgelegt (string|number|boolean), weil das vorhandene
 * `SceneInputs.extras`-Schema genau das erlaubt.
 *
 *  - `wmPosition`   → Eckanker (string)
 *  - `wmOpacity`    → 0..1
 *  - `wmSize`       → 24..400 (Höhe in Komp-Pixeln)
 *  - `wmPaddingPx`  → Eck-Abstand
 *  - `wmOffsetX/Y`  → optional, Feinjustage in % der Canvas
 *  - `wmXPct/YPct`  → optional, freier Mittelpunkt
 *
 * Wenn `wmXPct`/`wmYPct` per-Scene gesetzt sind, gewinnen sie immer.
 * Wenn nur eines davon gesetzt ist, fällt das andere auf den globalen
 * Wert zurück (wir mappen Member für Member, nicht das ganze Pärchen).
 */
type EffectiveWatermark = {
  position: WatermarkPosition;
  opacity: number;
  size: number;
  paddingPx: number;
  offsetX?: number;
  offsetY?: number;
  xPct?: number;
  yPct?: number;
};

function resolveSceneWatermark(
  global: NonNullable<QueueItem["watermark"]>,
  extras: Record<string, string | number | boolean> | undefined,
): EffectiveWatermark {
  const ex = (extras ?? {}) as Record<string, string | number | boolean>;
  const num = (k: string): number | undefined => {
    const v = ex[k];
    return typeof v === "number" ? v : undefined;
  };
  const pos = ex.wmPosition;
  const isPos = (s: unknown): s is WatermarkPosition =>
    s === "tl" || s === "tr" || s === "bl" || s === "br";
  return {
    position: isPos(pos) ? pos : global.position,
    opacity: num("wmOpacity") ?? global.opacity,
    size: num("wmSize") ?? global.size,
    paddingPx: num("wmPaddingPx") ?? global.paddingPx,
    offsetX: num("wmOffsetX") ?? global.offsetX,
    offsetY: num("wmOffsetY") ?? global.offsetY,
    xPct: num("wmXPct") ?? global.xPct,
    yPct: num("wmYPct") ?? global.yPct,
  };
}
